import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  sendGetRequest,
  sendPatchRequest,
  sendPostRequest,
} from '../../../api/requestUtil';
import { AppDispatch } from '../../../store/store';
import { fetchCommentUpvoteStatus } from '../../upvotes/actions/commentUpvoteActions';
import { fetchDiscussionUpvoteStatus } from '../../upvotes/actions/discussionUpvoteActions';
// import { getFeaturedArticleDiscussion } from '../../../store/actions/featuredArticlesActions';
import { getBlogDiscussionById } from '../../blogs/actions/blogPageActions';
import { getFeaturedArticleDiscussion } from '../../featuredArticles/actions/featuredArticlesActions';
import { fetchClearingDiscussions } from '../../terrain/actions/clearingActions';

// -----------------------------------------------------------
// 1. getDiscussionDetailsById (Input type change, URL change)
// -----------------------------------------------------------
function contentBasePath(contentType: 'blog' | 'article' | 'clearing'): string {
  if (contentType === 'article') return 'articles';
  if (contentType === 'clearing') return 'clearings';
  return 'blogs';
}

export const getDiscussionDetailsById = createAsyncThunk(
  'get-discussion-details-by-id',
  async ({
    contentId,
    discussionId,
    contentType,
  }: {
    contentId?: string;
    discussionId?: string;
    contentType: 'blog' | 'article' | 'clearing';
  }) => {
    const basePath = contentBasePath(contentType);
    try {
      const response = await sendGetRequest(
        // 💡 URL CHANGED to use dynamic basePath and contentId
        `/api/${basePath}/${contentId}/discussions/${discussionId}/`
      );
      return response;
    } catch (error) {}
  }
);

// -----------------------------------------------------------
// 2. getDiscussionComments (Input type change, URL change)
// -----------------------------------------------------------
export const getDiscussionComments = createAsyncThunk(
  'get-discussion-comments',
  async (
    {
      contentId,
      discussionId,
      page,
      contentType,
    }: {
      contentId: string;
      discussionId: string;
      page: number;
      contentType: 'blog' | 'article' | 'clearing';
    },
    { rejectWithValue }
  ) => {
    let queryParams = '';
    if (page) queryParams += `page=${page}&`;

    const basePath = contentBasePath(contentType);

    try {
      const response = await sendGetRequest(
        // 💡 URL CHANGED to use dynamic basePath and contentId
        `/api/${basePath}/${contentId}/discussions/${discussionId}/comments/?${queryParams}`
      );
      return { ...response, page };
    } catch (error) {
      return rejectWithValue('Failed to fetch discussion comments');
    }
  }
);

// -----------------------------------------------------------
// 3. addDiscussionComment (Input type change, URL change)
// -----------------------------------------------------------
export const addDiscussionComment = createAsyncThunk<
  CommentDetailsType,
  {
    contentId: string;
    discussionId: string;
    content: string;
    contentType: 'blog' | 'article' | 'clearing';
    parentId?: number;
    replyToId?: number;
  },
  { state: AppState; rejectValue: AddDiscussionCommentErrorType }
>(
  'add-discussion-comment',
  async (
    { contentId, discussionId, content, contentType, parentId, replyToId },
    { getState, rejectWithValue }
  ) => {
    const state = getState();
    const token = state.user.authToken;
    const basePath = contentBasePath(contentType);

    const body: { content: string; parent_id?: number; reply_to_id?: number } = { content };
    if (parentId !== undefined) body.parent_id = parentId;
    if (replyToId !== undefined) body.reply_to_id = replyToId;

    try {
      const response = await sendPostRequest(
        `/api/${basePath}/${contentId}/discussions/${discussionId}/comments/`,
        body,
        token
      );
      return response as CommentDetailsType;
    } catch (error: any) {
      return rejectWithValue({
        detail: error.detail || error.message || 'Failed to add comment',
      });
    }
  }
);

// -----------------------------------------------------------
// 4. editDiscussionComment (Input type change, URL change)
// -----------------------------------------------------------
export const editDiscussionComment = createAsyncThunk<
  CommentDetailsType,
  {
    contentId: string;
    discussionId: string;
    commentId: number;
    content: string;
    contentType: 'blog' | 'article' | 'clearing';
  },
  { state: AppState; rejectValue: { detail: string } }
>(
  'edit-discussion-comment',
  async (
    { contentId, discussionId, commentId, content, contentType },
    { getState, rejectWithValue }
  ) => {
    const state = getState();
    const token = state.user.authToken;
    const basePath = contentBasePath(contentType);

    try {
      const response = await sendPatchRequest(
        // 💡 URL CHANGED to use dynamic basePath and contentId
        `/api/${basePath}/${contentId}/discussions/${discussionId}/comments/${commentId}/edit/`,
        { content },
        token
      );
      // The backend returns { detail: "...", comment: {...} }
      return response.comment as CommentDetailsType;
    } catch (error: any) {
      return rejectWithValue({
        detail:
          error.response?.data?.detail ||
          error.detail ||
          'Failed to edit comment',
      });
    }
  }
);

// -----------------------------------------------------------
// 5. fetchFullDiscussionPage (Helper thunk calls updated)
// -----------------------------------------------------------
export const fetchFullDiscussionPage = createAsyncThunk<
  void,
  { contentId: string; discussionId: string; contentType: 'blog' | 'article' | 'clearing' },
  { state: AppState; dispatch: AppDispatch }
>(
  'fetch-full-discussion-page',
  // 💡 CHANGED blogId to contentId
  async ({ contentId, discussionId, contentType }, { dispatch, getState }) => {
    // Dispatch reset actions
    dispatch({ type: 'discussionDetailStateSlice/resetDiscussionState' });
    dispatch({ type: 'commentUpvote/resetCommentUpvotes' });

    // Fetch the main discussion details and comments in parallel
    await Promise.all([
      // 💡 Pass contentId and contentType
      dispatch(
        getDiscussionDetailsById({ contentId, discussionId, contentType })
      ),
      // 💡 Pass contentId and contentType
      dispatch(
        getDiscussionComments({ contentId, discussionId, page: 1, contentType })
      ),
    ]);

    // Update discussion upvote status
    await dispatch(
      fetchDiscussionUpvoteStatus({
        contentId: Number(contentId), // 💡 CHANGED blogId to contentId
        discussionId: Number(discussionId),
        contentType: contentType,
      })
    );

    const state = getState();
    const comments = state.discussion.commentDetails;

    // Update comment upvote status (top-level comments and their replies)
    comments.forEach(comment => {
      dispatch(
        fetchCommentUpvoteStatus({
          contentId: Number(contentId),
          discussionId: Number(discussionId),
          commentId: comment.id,
          contentType: contentType,
        })
      );
      (comment.replies || []).forEach(reply => {
        dispatch(
          fetchCommentUpvoteStatus({
            contentId: Number(contentId),
            discussionId: Number(discussionId),
            commentId: reply.id,
            contentType: contentType,
          })
        );
      });
    });
  }
);

// -----------------------------------------------------------
// 6. editBlogDiscussion (Input type change, URL change)
// -----------------------------------------------------------
export const editBlogDiscussion = createAsyncThunk<
  DiscussionDetailsType,
  {
    contentId: string;
    discussionId: string;
    title: string;
    content: string;
    contentType: 'blog' | 'article' | 'clearing';
  },
  { state: AppState; rejectValue: { detail: string } }
>(
  'edit-blog-discussion',
  async (
    { contentId, discussionId, title, content, contentType },
    { getState, rejectWithValue }
  ) => {
    const state = getState();
    const token = state.user.authToken;
    const basePath = contentBasePath(contentType);

    try {
      const response = await sendPatchRequest(
        // 💡 URL CHANGED to use dynamic basePath and contentId
        `/api/${basePath}/${contentId}/discussions/${discussionId}/edit/`,
        { title, content },
        token
      );
      // The backend returns { detail: "...", discussion: {...} }
      return response.discussion as DiscussionDetailsType;
    } catch (error: any) {
      return rejectWithValue({
        detail:
          error.response?.data?.detail ||
          error.detail ||
          'Failed to edit discussion',
      });
    }
  }
);

// -----------------------------------------------------------
// 7. addBlogArticleDiscussion (Already generic, no change needed)
// -----------------------------------------------------------
export const addBlogArticleDiscussion = createAsyncThunk<
  { newDiscussion: any; contentType: 'blog' | 'article' | 'clearing'; contentId: number },
  AddDiscussionArgs,
  { state: AppState; rejectValue: { detail: string } }
>(
  'add-blog-article-discussion',
  async (data: AddDiscussionArgs, { dispatch, getState, rejectWithValue }) => {
    const { contentId, contentType, title, content } = data;
    const state = getState() as AppState;
    const token = state.user.authToken;

    // console.log('🔍 [DEBUG] Starting discussion creation:', {
    //   contentId,
    //   contentType,
    //   title,
    //   content,
    //   hasToken: !!token,
    // });

    const baseUrl = contentBasePath(contentType);
    const contentIdString = String(contentId);
    const url = `/api/${baseUrl}/${contentIdString}/discussions/create/`;

    // console.log('🔍 [DEBUG] API URL:', url);

    try {
      const response = await sendPostRequest(url, { title, content }, token);

      // console.log('🔍 [DEBUG] Full API Response:', response);

      // ✅ FIX: Remove the status check entirely - just check if response exists
      if (response) {
        // console.log('🔍 [DEBUG] Discussion created successfully! Response has data, dispatching refresh actions...');

        // Dispatch refresh actions to update the lists
        if (contentType === 'article') {
          dispatch(getFeaturedArticleDiscussion(contentId));
        } else if (contentType === 'clearing') {
          dispatch(fetchClearingDiscussions(contentId));
        } else {
          dispatch(getBlogDiscussionById(contentId));
        }

        // ✅ RETURN the new discussion data for immediate UI update
        return {
          newDiscussion: response, // response IS the discussion data
          contentType,
          contentId,
        };
      } else {
        console.error('🔍 [DEBUG] No response received');
        return rejectWithValue({
          detail: 'No response received from server',
        });
      }
    } catch (error: any) {
      console.error('🔍 [DEBUG] API Error:', error);

      return rejectWithValue({
        detail:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          'Failed to create discussion',
      });
    }
  }
);
